import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from './PublicLayout';
import SEO from '../common/SEO';
import { Calendar, ArrowLeft, User, Share2 } from 'lucide-react';

const NewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        console.error('API URL not configured');
        return;
      }
      const response = await fetch(`${API_BASE}/news/public/${id}`);
      const data = await response.json();

      if (data.success) {
        setArticle(data.data);
      } else {
        setError('Article not found');
      }
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="container py-5">
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !article) {
    return (
      <PublicLayout>
        <div className="container py-5">
          <div className="alert alert-warning text-center" role="alert">
            {error || 'Article not found'}
          </div>
          <div className="text-center">
            <button
              onClick={() => navigate('/news')}
              className="btn text-white"
              style={{ backgroundColor: '#6B8E23' }}
            >
              <ArrowLeft size={16} className="me-2" />
              Back to News
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  if (!API_BASE) return null;
  const imageUrl = article.image_url && article.image_url.startsWith('/uploads/')
    ? `${API_BASE}${article.image_url}`
    : article.image_url;

  // Social sharing functions
  const shareUrl = window.location.href;
  const shareTitle = article.title;

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    const shareLinks = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
      email: `mailto:?subject=${encodedTitle}&body=Check out this article: ${encodedUrl}`
    };

    if (shareLinks[platform]) {
      if (platform === 'email') {
        window.location.href = shareLinks[platform];
      } else {
        window.open(shareLinks[platform], '_blank', 'width=600,height=400');
      }
    }
  };

  const shareImage = '/arcanews.logo.jpg';

  return (
    <PublicLayout>
      <SEO
        title={`${article.title} | ARCA News`}
        description={article.excerpt || (article.content ? article.content.substring(0, 160) + '...' : 'Read the latest news from ARCA — Africa Respiratory Care Association.')}
        keywords={`ARCA news, ${article.category || 'news'}, respiratory care Africa, ${article.title}`}
        image={shareImage}
        url={`/news/${id}`}
      />
      <div className="py-5">
        <div className="container">
          {/* Back Button */}
          <button
            onClick={() => navigate('/news')}
            className="btn btn-outline-secondary mb-4"
          >
            <ArrowLeft size={16} className="me-2" />
            Back to News
          </button>

          {/* Article Header */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Category Badge */}
              <span className="badge text-white mb-3" style={{ backgroundColor: '#6B8E23', fontSize: '0.9rem' }}>
                {article.category}
              </span>

              {/* Title */}
              <h1 className="display-5 fw-bold text-dark mb-4">{article.title}</h1>

              {/* Meta Information */}
              <div className="d-flex align-items-center text-muted mb-4 pb-4 border-bottom">
                <User size={18} className="me-2" />
                <span className="me-3">{article.author}</span>
                <Calendar size={18} className="me-2" />
                <span>
                  {new Date(article.published_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                {article.featured && (
                  <>
                    <span className="mx-3">•</span>
                    <span className="badge bg-warning text-dark">Featured</span>
                  </>
                )}
              </div>

              {/* Featured Image */}
              {imageUrl && (
                <div className="mb-5">
                  <img
                    src={imageUrl}
                    alt={article.title}
                    className="img-fluid rounded shadow-lg w-100"
                    style={{ maxHeight: '600px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Article Content */}
              <div className="article-content">
                {/* Excerpt */}
                {article.excerpt && (
                  <div className="lead text-muted mb-4 p-4 bg-light rounded border-start border-5"
                    style={{ borderColor: '#6B8E23' }}>
                    {article.excerpt}
                  </div>
                )}

                {/* Full Content */}
                {article.content ? (
                  <div className="fs-5 text-dark lh-lg" style={{ textAlign: 'justify' }}>
                    {article.content.split('\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                ) : (
                  <div className="fs-5 text-dark lh-lg" style={{ textAlign: 'justify' }}>
                    <p className="mb-4">{article.excerpt}</p>
                  </div>
                )}
              </div>

              {/* Share Section */}
              <div className="mt-5 pt-4 border-top">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div className="mb-3 mb-md-0">
                    <h5 className="mb-0 d-flex align-items-center">
                      <Share2 size={20} className="me-2" style={{ color: '#6B8E23' }} />
                      Share this article
                    </h5>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                      onClick={() => handleShare('facebook')}
                      title="Share on Facebook"
                    >
                      <i className="fab fa-facebook-f me-2"></i>
                      Facebook
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                      onClick={() => handleShare('twitter')}
                      title="Share on Twitter"
                    >
                      <i className="fab fa-twitter me-2"></i>
                      Twitter
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                      onClick={() => handleShare('linkedin')}
                      title="Share on LinkedIn"
                    >
                      <i className="fab fa-linkedin-in me-2"></i>
                      LinkedIn
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                      onClick={() => handleShare('email')}
                      title="Share via Email"
                    >
                      <i className="fas fa-envelope me-2"></i>
                      Email
                    </button>
                  </div>
                </div>
              </div>

              {/* Back to News Button */}
              <div className="text-center mt-5">
                <button
                  onClick={() => navigate('/news')}
                  className="btn btn-lg text-white fw-bold px-5"
                  style={{ backgroundColor: '#6B8E23' }}
                >
                  <ArrowLeft size={20} className="me-2" />
                  Back to All News
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default NewsDetailPage;
